"use strict";
import { useAccount } from "hooks/useAccount";
import { useEthersWeb3Provider } from "hooks/useEthersProvider";
import { useEffect, useMemo } from "react";
import { usePendingTransactions } from "state/transactions/hooks";
import { TransactionStatus } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { logger } from "utilities/src/logger/logger";
import { useEvent } from "utilities/src/react/hooks";
function usePendingBatches() {
  const transactions = usePendingTransactions();
  const account = useAccount();
  return useMemo(() => {
    if (account.status === "connected") {
      const connectorId = account.connector.id;
      const shouldAttemptCheck = (tx) => {
        const isBatch = tx.batchInfo !== void 0;
        const batchConnectorId = tx.batchInfo?.connectorId;
        return Boolean(isBatch && batchConnectorId === connectorId);
      };
      return transactions.filter(shouldAttemptCheck);
    }
    return [];
  }, [account.status, account.connector?.id, transactions]);
}
const FAILURE_COUNT_THRESHOLD = 4;
const FAILURE_COUNT_MAP = {};
function finalizeBatch(params) {
  const { transaction, onActivityUpdate, hash, status } = params;
  onActivityUpdate({
    type: "transaction",
    chainId: transaction.batchInfo.chainId,
    update: { ...transaction, status, hash },
    original: transaction
  });
  delete FAILURE_COUNT_MAP[transaction.batchInfo.batchId];
}
export function usePollPendingBatchTransactions(onActivityUpdate) {
  const pendingBatchTransactions = usePendingBatches();
  const walletProvider = useEthersWeb3Provider();
  const pollAllPendingBatches = useEvent(async (provider) => {
    for (const transaction of pendingBatchTransactions) {
      try {
        const result = await getCallsStatus({ provider, batchId: transaction.batchInfo.batchId });
        const receipt = result?.receipts?.[0];
        if (result?.status === 200) {
          if (!receipt) {
            throw new Error(
              `${transaction.batchInfo.connectorId ?? "wallet"} breaks eip5972 spec, returning a 200 status with no receipt`
            );
          }
          const hash = receipt.transactionHash;
          const updatedStatus = receipt.status === "0x1" ? TransactionStatus.Confirmed : TransactionStatus.Failed;
          finalizeBatch({ transaction, onActivityUpdate, hash, status: updatedStatus });
        }
        if (result?.status >= 400) {
          if (receipt) {
            const hash = receipt.transactionHash;
            finalizeBatch({ transaction, onActivityUpdate, hash, status: TransactionStatus.Failed });
            return;
          }
          throw new Error(
            `Failure status of ${result.status} received from ${transaction.batchInfo.connectorId ?? "wallet"} with no receipt`
          );
        }
      } catch (error) {
        FAILURE_COUNT_MAP[transaction.batchInfo.batchId] = (FAILURE_COUNT_MAP[transaction.batchInfo.batchId] ?? 0) + 1;
        if (FAILURE_COUNT_MAP[transaction.batchInfo.batchId] >= FAILURE_COUNT_THRESHOLD) {
          const connectorId = transaction.batchInfo.connectorId;
          logger.error(error, { tags: { file: "batch.ts", function: "pollAllPendingBatches" }, extra: { connectorId } });
          finalizeBatch({ transaction, onActivityUpdate, status: TransactionStatus.Failed });
        }
      }
    }
  });
  useEffect(() => {
    const interval = walletProvider && pendingBatchTransactions.length > 0 ? setInterval(() => pollAllPendingBatches(walletProvider), 3e3) : void 0;
    return () => clearInterval(interval);
  }, [pendingBatchTransactions, pollAllPendingBatches, walletProvider]);
}
function getCallsStatus(params) {
  const { provider, batchId } = params;
  return provider.send("wallet_getCallsStatus", [batchId]);
}
